// Each stage's curve: the reader's score after every ply of the stage, and
// the reader's moves in it the review lists (a blunder or a miss), so a
// "last game" chart can draw the stage and mark where it went wrong. Same
// stage split and judgements as the stage errors, so their counts agree.
// With clock comments in the PGN, the curve also carries the reader's clock
// ply by ply (2026-10-10), for the strip under the chart.
import { type FactExtractor, type ScannedGame } from '../extractor.ts';
import { type GameFacts, type Phase, type StageCurve } from '@chess/api-model';

const PHASES: readonly Phase[] = ['opening', 'middlegame', 'ending'];

export const stageCurves: FactExtractor = {
  id: 'stage-curves',
  extract(game: ScannedGame, facts: GameFacts): void {
    const curves: Partial<Record<Phase, StageCurve>> = {};
    for (const phase of PHASES) {
      const { fromPly, toPly } = game.range(phase);
      if (toPly <= fromPly) continue;
      const scores: Array<number | null> = [];
      for (let ply = fromPly; ply <= toPly; ply++) scores.push(game.pawnsAt(ply) ?? null);
      const marks: StageCurve['marks'] = [];
      for (let ply = fromPly + 1; ply <= toPly; ply++) {
        const move = game.moves[ply - 1];
        if (!move || !game.isReaders(ply)) continue;
        const kind = game.judge.kind(ply, move, game.reader);
        if (kind) marks.push({ ply, san: move.san, kind });
      }
      const clock = readerClock(game, fromPly, toPly);
      curves[phase] = clock ? { fromPly, toPly, scores, marks, clock } : { fromPly, toPly, scores, marks };
    }
    facts.stageCurves = curves;
  },
};

/**
 * The reader's seconds left at each ply from `fromPly` to `toPly`: after the
 * reader's own move its clock, at the opponent's the reader's last known
 * value (from before the stage too), null before any is known. Undefined when
 * the PGN has no clocks.
 */
function readerClock(game: ScannedGame, fromPly: number, toPly: number): Array<number | null> | undefined {
  if (!game.clocks.some((c) => c !== undefined)) return undefined;
  let last: number | null = null;
  for (let ply = 1; ply < fromPly; ply++) if (game.isReaders(ply)) last = game.clockAfter(ply) ?? last;
  const out: Array<number | null> = [];
  for (let ply = fromPly; ply <= toPly; ply++) {
    if (ply >= 1 && game.isReaders(ply)) last = game.clockAfter(ply) ?? last;
    out.push(last);
  }
  return out;
}
